'use client';
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
   Box,
   Flex,
   Text,
   useColorModeValue,
   Button,
   Popover,
   PopoverTrigger,
   PopoverContent,
   PopoverBody,
   Table,
   Thead,
   Tbody,
   Tr,
   Grid,
   Td,
   Th,
   useToast,
   VStack,
   TabPanel,
   TabPanels,
   Tabs,
   Tab,
   TabList,
   MenuList,
   MenuButton,
   Menu,
} from '@chakra-ui/react';
import Card from 'components/card/Card';
import Download from '../../../../assets/img/nfts/download.svg';
import xls from '../../../../../src/assets/img/nfts/xls.svg';
import json from '../../../../../src/assets/img/nfts/json.svg';
import back from '../../../../../src/assets/img/nfts/back.svg';
import { useSelector, useDispatch } from 'react-redux';
import PeriodDatePicker from 'components/RangePicker/PeriodDatePicker';
import { getGSTR3B } from '../../../../redux/actions/reports';
import { ChevronDownIcon } from '@chakra-ui/icons';

const GSTR3B = () => {
   const textColor = useColorModeValue('secondaryGray.900', 'white');
   const backgroundColor = useColorModeValue('white', '#101F4A');
   const tableHeaderBg = useColorModeValue('#f7f7f7', '#1a2a52');
   const tableRowHover = useColorModeValue('#f9f9f9', '#1a3157');
   const borderColor = useColorModeValue('#e2e8f0', '#2d3a5a');
   const [selected, setSelected] = useState(null);
   const comapanyList = useSelector((state) => state.company.list || []);
   const financialYear = localStorage.getItem('selectedFinancialYear');
   const companyId = localStorage.getItem('companyId');
   const toast = useToast();
   const canvasRef = useRef();
   const dispatch = useDispatch();

   const [dateRange, setDateRange] = useState(() => {
      const today = new Date();
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(today.getDate() - 6);

      return {
         fromDate: sevenDaysAgo.toISOString().split('T')[0], // last 7th day
         toDate: today.toISOString().split('T')[0], // today
      };
   });
   const [companyInfo, setCompanyInfo] = useState({
      name: '',
      address: '',
      gstin: '',
   });

   const { gstr3b: gstr3bData = [], error } = useSelector((state) => state.reports);
   console.log('gstr3bData', gstr3bData);
   const groupedData = gstr3bData.reduce((acc, item) => {
      if (!acc[item.para]) {
         acc[item.para] = [];
      }
      acc[item.para].push(item);
      return acc;
   }, {});

   const formatCurrency = (amount) => {
      if (amount === null || amount === undefined) return '0.00';
      return parseFloat(amount).toFixed(2);
   };

   const numericCompanyId = useMemo(() => Number(localStorage.getItem('companyId')), []);
   const selectedCompany = useMemo(
      () => comapanyList.find((c) => c.id === numericCompanyId),
      [comapanyList, numericCompanyId],
   );

   useEffect(() => {
      const fetchData = async () => {
         const companyId = localStorage.getItem('companyId');
         if (!companyId) {
            toast({
               title: 'Error',
               description: 'Company ID not found',
               status: 'error',
               duration: 5000,
               isClosable: true,
            });
            return;
         }

         if (selectedCompany) {
            setCompanyInfo((prev) => {
               if (
                  prev.name !== selectedCompany.name ||
                  prev.address !== (selectedCompany.address1 || '') + (selectedCompany.address2 || '') ||
                  prev.gstin !== selectedCompany.gstRegNo
               ) {
                  return {
                     name: selectedCompany.name || '',
                     address: (selectedCompany.address1 || '') + (selectedCompany.address2 || ''),
                     gstin: selectedCompany.gstRegNo || '',
                  };
               }
               return prev; // prevent unnecessary state update
            });
         }

         // Only dispatch if both dates are set
         if (dateRange.fromDate && dateRange.toDate) {
            try {
               await dispatch(
                  getGSTR3B({
                     companyId: numericCompanyId,
                     fromDate: dateRange.fromDate,
                     toDate: dateRange.toDate,
                  }),
               );
            } catch (err) {
               toast({
                  title: 'Error',
                  description: 'Failed to fetch GSTR3B data',
                  status: 'error',
                  duration: 5000,
                  isClosable: true,
               });
            }
         }
      };

      if (dateRange.fromDate && dateRange.toDate) {
         fetchData();
      }
   }, [dateRange, dispatch, toast, comapanyList, numericCompanyId, selectedCompany]);

   const handleDateRangeChange = (dates) => {
      if (!dates || dates.length !== 2) return;

      const [from, to] = dates.map((date) => {
         if (typeof date === 'string') return date;
         return date.toISOString().split('T')[0];
      });

      setDateRange({
         fromDate: from,
         toDate: to,
      });
   };

   const generateEnhancedHTMLToPDF = () => {
      // Calculate totals from dynamic data
      const calculateTotals = () => {
         const section31Data = gstr3bData.filter((item) => item.para.includes('3.1'));

         return {
            totalTaxableValue: section31Data.reduce((sum, item) => sum + (parseFloat(item.taxable_value) || 0), 0),
            totalIGST: section31Data.reduce((sum, item) => sum + (parseFloat(item.igst_amount) || 0), 0),
            totalCGST: section31Data.reduce((sum, item) => sum + (parseFloat(item.cgst_amount) || 0), 0),
            totalSGST: section31Data.reduce((sum, item) => sum + (parseFloat(item.sgst_amount) || 0), 0),
            totalTax: section31Data.reduce((sum, item) => sum + (parseFloat(item.total_tax) || 0), 0),
         };
      };

      const totals = calculateTotals();
      const section31Data = gstr3bData.filter((item) => item.para.includes('3.1'));

      // Define default rows if no data exists
      const defaultSection31Rows = [
         {
            subpara: 'A. Outward taxable supplies ( other than zero rated, nil rated and exempted )',
            taxable_value: totals.totalTaxableValue || 0,
            igst_amount: totals.totalIGST || 0,
            cgst_amount: totals.totalCGST || 0,
            sgst_amount: totals.totalSGST || 0,
         },
         {
            subpara: 'B. Outward taxable supplies ( zero rated )',
            taxable_value: 0,
            igst_amount: 0,
            cgst_amount: 0,
            sgst_amount: 0,
         },
         {
            subpara: 'C. Other outward supplies ( Exempted )',
            taxable_value: 0,
            igst_amount: 0,
            cgst_amount: 0,
            sgst_amount: 0,
         },
         {
            subpara: 'D. Inward supplies ( liable to reverse charge )',
            taxable_value: 0,
            igst_amount: 0,
            cgst_amount: 0,
            sgst_amount: 0,
         },
         { subpara: 'E. Non-GST outward supplies', taxable_value: 0, igst_amount: 0, cgst_amount: 0, sgst_amount: 0 },
      ];

      const dataToDisplay = section31Data.length > 0 ? section31Data : defaultSection31Rows;
      const totalTaxLiability = totals.totalIGST + totals.totalCGST + totals.totalSGST;

      const htmlContent = `
    <!DOCTYPE html>
    <html>
    <head>
      <title>GST Summary (For 3B)</title>
      <style>
        @page { 
          size: A4; 
          margin: 15mm; 
        }
        * { 
          box-sizing: border-box; 
          margin: 0; 
          padding: 0; 
        }
        body { 
          font-family: 'Arial', sans-serif; 
          font-size: 10px;
          line-height: 1.2;
          color: #000;
          background: #fff;
          padding:50px;
        }
        .header { 
          text-align: center; 
          margin-bottom: 15px; 
          border-bottom: 2px solid #000;
          padding-bottom: 10px;
        }
        .header h1 { 
          font-size: 16px; 
          font-weight: bold; 
          margin-bottom: 5px;
        }
        .company-info { 
          text-align: center; 
          margin-bottom: 20px; 
          padding: 10px 0;
        }
        .company-name { 
          font-size: 13px; 
          font-weight: bold; 
          margin-bottom: 5px;
        }
        .company-address { 
          font-size: 9px; 
          margin-bottom: 5px;
          line-height: 1.3;
        }
        .gstin { 
          font-weight: bold; 
          font-size: 11px;
        }
        .section-title { 
          font-weight: bold; 
          margin: 15px 0 8px 0; 
          font-size: 11px;
        }
        table { 
          width: 100%; 
          border-collapse: collapse; 
          margin-bottom: 15px; 
          font-size: 9px;
        }
       th, td {
            padding: 10px;
            text-align: left;
            border-bottom: 1px dotted #ddd;
        }
            th {
            background-color: #f5f5f5;
            font-weight: bold;
            border-bottom: 1px solid #333;
            border-top: 1px solid #333;
        }
        td { 
          font-size: 8px;
        }
        .text-right { 
          text-align: right; 
        }
        .text-center { 
          text-align: center; 
        }
        .text-left { 
          text-align: left; 
        }
        .font-bold { 
          font-weight: bold; 
        }
        .dotted-line {
          border-bottom: 1px dotted #000;
          height: 1px;
          margin: 8px 10px;
        }
        .empty-cell {
          position: relative;
        }
        .empty-cell::after {
          content: "";
          position: absolute;
          left: 10px;
          right: 10px;
          top: 50%;
          height: 1px;
          border-bottom: 1px dotted #000;
        }
        .notes { 
          font-size: 8px; 
          margin-top: 15px; 
          line-height: 1.4;
        }
        .page-number { 
          margin-top: 20px; 
          text-align: right; 
          font-size: 8px;
        }
        .total-row { 
          background-color: #f0f0f0; 
          font-weight: bold; 
        }
        .payment-table th {
          font-size: 8px;
          padding: 2px;
        }
        .payment-table td {
          font-size: 8px;
          padding: 2px;
        }
      </style>
    </head>
    <body>      
      <div class="company-info">
        <div class="company-name">${companyInfo.name}</div>
        <div class="company-address">${companyInfo.address}</div>
        <div class="gstin">GSTIN: ${companyInfo.gstin}</div>
        <div class="gst-summary"> GST Summary ( For 3B ) </div>
      </div>

      <div class="section-title">Period: ${dateRange.fromDate} TO ${dateRange.toDate}</div>
      <div class="section-title">3.1 Details of outward supplies and inward supplies liable to reverse charge.</div>
      <table>
        <thead>
          <tr>
            <th style="width: 40%">Nature Of Supply</th>
            <th style="width: 12%">Total Taxable<br>Value</th>
            <th style="width: 12%">Integrated<br>Tax</th>
            <th style="width: 12%">Central<br>Tax</th>
            <th style="width: 12%">State/UT<br>Tax</th>
            <th style="width: 12%">CESS</th>
          </tr>
        </thead>
        <tbody>
          ${dataToDisplay
             .map(
                (item) => `
            <tr>
              <td class="text-left">${item.subpara || item.description || 'N/A'}</td>
              <td class="text-right">${formatCurrency(item.taxable_value || 0)}</td>
              <td class="text-right">${formatCurrency(item.igst_amount || 0)}</td>
              <td class="text-right">${formatCurrency(item.cgst_amount || 0)}</td>
              <td class="text-right">${formatCurrency(item.sgst_amount || 0)}</td>
              <td class="text-right">0.00</td>
            </tr>
          `,
             )
             .join('')}
        </tbody>
      </table>

      <div class="section-title">3.2 of the supplies shown in 3.1 (a) above, details of inter-state supplies made to unregistered persons, composition taxable persons and UIN holders</div>
      <table>
        <thead>
          <tr>
            <th style="width: 45%">Nature Of Supply</th>
            <th style="width: 20%">Place of Supply</th>
            <th style="width: 20%">Total Taxable<br>Value</th>
            <th style="width: 15%">Integrated<br>Tax</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td class="text-left">A. Supplies made to Unregistered Persons</td>
            <td class="text-center empty-cell"></td>
            <td class="text-right">0.00</td>
            <td class="text-right">0.00</td>
          </tr>
          <tr>
            <td class="text-left">B. Supplies made to Composition Taxable Persons</td>
            <td class="text-center empty-cell"></td>
            <td class="text-right">0.00</td>
            <td class="text-right">0.00</td>
          </tr>
          <tr>
            <td class="text-left">C. Supplies made to UIN Holder</td>
            <td class="text-center empty-cell"></td>
            <td class="text-right">0.00</td>
            <td class="text-right">0.00</td>
          </tr>
        </tbody>
      </table>

      <div class="section-title">4.0 Eligible ITC</div>
      <table>
        <thead>
          <tr>
            <th style="width: 52%">Details</th>
            <th style="width: 12%">Integrated<br>Tax</th>
            <th style="width: 12%">Central<br>Tax</th>
            <th style="width: 12%">State/UT<br>Tax</th>
            <th style="width: 12%">CESS</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td class="text-left font-bold">A. ITC Available ( whether in full or part )</td>
            <td></td>
            <td></td>
            <td></td>
            <td></td>
          </tr>
          <tr>
            <td class="text-left">(1) Import of goods</td>
            <td class="text-right">0.00</td>
            <td class="text-right">0.00</td>
            <td class="text-right">0.00</td>
            <td class="text-right">0.00</td>
          </tr>
          <tr>
            <td class="text-left">(2) Import of Services</td>
            <td class="text-right">0.00</td>
            <td class="text-right">0.00</td>
            <td class="text-right">0.00</td>
            <td class="text-right">0.00</td>
          </tr>
          <tr>
            <td class="text-left">(3) Inward supplies liable to reverse charge ( other than 1 & 2 above )</td>
            <td class="text-right">0.00</td>
            <td class="text-right">0.00</td>
            <td class="text-right">0.00</td>
            <td class="text-right">0.00</td>
          </tr>
          <tr>
            <td class="text-left">(4) Inward supplies from ISD</td>
            <td class="text-right">0.00</td>
            <td class="text-right">0.00</td>
            <td class="text-right">0.00</td>
            <td class="text-right">0.00</td>
          </tr>
          <tr>
            <td class="text-left">(5) All Other ITC</td>
            <td class="text-right">0.00</td>
            <td class="text-right">0.00</td>
            <td class="text-right">0.00</td>
            <td class="text-right">0.00</td>
          </tr>
          <tr>
            <td class="text-left font-bold">B. ITC Reversed</td>
            <td></td>
            <td></td>
            <td></td>
            <td></td>
          </tr>
          <tr>
            <td class="text-left">(1) ITC Reversal</td>
            <td class="text-right">0.00</td>
            <td class="text-right">0.00</td>
            <td class="text-right">0.00</td>
            <td class="text-right">0.00</td>
          </tr>
          <tr class="total-row">
            <td class="text-left font-bold">C. Net ITC Available<br>C = (A)-(B)</td>
            <td class="text-right font-bold">0.00</td>
            <td class="text-right font-bold">0.00</td>
            <td class="text-right font-bold">0.00</td>
            <td class="text-right font-bold">0.00</td>
          </tr>
          <tr>
            <td class="text-left font-bold">D. Ineligible ITC</td>
            <td></td>
            <td></td>
            <td></td>
            <td></td>
          </tr>
          <tr>
            <td class="text-left">(1) Ineligible ITC</td>
            <td class="text-right">0.00</td>
            <td class="text-right">0.00</td>
            <td class="text-right">0.00</td>
            <td class="text-right">0.00</td>
          </tr>
        </tbody>
      </table>

      <div class="section-title">5.0 Value of Exempt, nil-rated and non-GST inward supplies</div>
      <table>
        <thead>
          <tr>
            <th style="width: 60%">Nature Of Supply</th>
            <th style="width: 20%">Inter-State Supplies</th>
            <th style="width: 20%">Intra-State Supplies</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td class="text-left">From a supplied under composition scheme, Exempt and Nil rated supply</td>
            <td class="text-right">0.00</td>
            <td class="text-right">0.00</td>
          </tr>
          <tr>
            <td class="text-left">Non GST Supply</td>
            <td class="text-right">0.00</td>
            <td class="text-right">0.00</td>
          </tr>
        </tbody>
      </table>

      <div class="section-title">6.1 Payment of Tax</div>
      <table class="payment-table">
        <thead>
          <tr>
            <th style="width: 12%">Description</th>
            <th style="width: 12%">Tax<br>Liability</th>
            <th style="width: 12%">ITC<br>Credit</th>
            <th style="width: 12%">GST<br>Payable</th>
            <th style="width: 12%">Excess<br>Credit</th>
            <th style="width: 12%">Credit<br>Adjusted</th>
            <th style="width: 14%">Net GST<br>Payable</th>
            <th style="width: 14%">Balance C/F<br>Credit</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td class="text-left font-bold">IGST</td>
            <td class="text-right">${formatCurrency(totals.totalIGST)}</td>
            <td class="text-right">0.00</td>
            <td class="text-right">${formatCurrency(totals.totalIGST)}</td>
            <td class="text-right">0.00</td>
            <td class="text-right">0.00</td>
            <td class="text-right">${formatCurrency(totals.totalIGST)}</td>
            <td class="text-right">0.00</td>
          </tr>
          <tr>
            <td class="text-left font-bold">CGST</td>
            <td class="text-right">${formatCurrency(totals.totalCGST)}</td>
            <td class="text-right">0.00</td>
            <td class="text-right">${formatCurrency(totals.totalCGST)}</td>
            <td class="text-right">0.00</td>
            <td class="text-right">0.00</td>
            <td class="text-right">${formatCurrency(totals.totalCGST)}</td>
            <td class="text-right">0.00</td>
          </tr>
          <tr>
            <td class="text-left font-bold">UT/SGST</td>
            <td class="text-right">${formatCurrency(totals.totalSGST)}</td>
            <td class="text-right">0.00</td>
            <td class="text-right">${formatCurrency(totals.totalSGST)}</td>
            <td class="text-right">0.00</td>
            <td class="text-right">0.00</td>
            <td class="text-right">${formatCurrency(totals.totalSGST)}</td>
            <td class="text-right">0.00</td>
          </tr>
          <tr>
            <td class="text-left font-bold">CESS</td>
            <td class="text-right">0.00</td>
            <td class="text-right">0.00</td>
            <td class="text-right">0.00</td>
            <td class="text-right">0.00</td>
            <td class="text-right">0.00</td>
            <td class="text-right">0.00</td>
            <td class="text-right">0.00</td>
          </tr>
          <tr class="total-row">
            <td class="text-left font-bold">TOTAL</td>
            <td class="text-right font-bold">${formatCurrency(totalTaxLiability)}</td>
            <td class="text-right font-bold">0.00</td>
            <td class="text-right font-bold">${formatCurrency(totalTaxLiability)}</td>
            <td class="text-right font-bold">0.00</td>
            <td class="text-right font-bold">0.00</td>
            <td class="text-right font-bold">${formatCurrency(totalTaxLiability)}</td>
            <td class="text-right font-bold">0.00</td>
          </tr>
        </tbody>
      </table>

      <div class="notes">
        <strong>Notes >></strong> The ITC Credit Amount shown above does not include previous months' credits, So to calculate the Net GST payable, reduce the previous months' credits (if any) from this amount.<br><br>
        <strong>NOTE**</strong> The above report is purely based on the data entered. Please ensure that state and GSTIN entered in the Debtors & Creditors Account are correct and HSN Code & Tax percentage specified in the Items are specified correctly before giving the report. If you find any difference in above figures, please edit the Sale/Purchase bill. To know more about Form GSTR 3B, please contact your GST Tax Practitioner.
      </div>

      <div class="page-number">Page #: 1</div>
    </body>
    </html>
  `;

      // Create a new window and print the HTML content
      const printWindow = window.open('', '_blank');
      printWindow.document.write(htmlContent);
      printWindow.document.close();

      // Wait for content to load then print
      printWindow.onload = function () {
         printWindow.print();
         printWindow.close();
      };

      toast({
         title: 'PDF Generated Successfully',
         description: 'GSTR-3B report with dynamic data is ready for printing',
         status: 'success',
         duration: 3000,
         isClosable: true,
      });
   };

   const formatCurrencyWithSmallDecimal = useCallback((value, showRupeeSymbol = false) => {
      if (value === null || value === undefined || value === '') return showRupeeSymbol ? '₹-' : '-';
      const numValue = Number(value);
      if (isNaN(numValue)) return showRupeeSymbol ? '₹' + String(value) : String(value);

      const formatted = Math.abs(numValue).toLocaleString('en-IN', {
         minimumFractionDigits: 2,
         maximumFractionDigits: 2,
      });

      // Split into integer and decimal parts for smaller decimal font
      const parts = formatted.split('.');

      if (parts.length === 2) {
         const formattedElement = (
            <>
               {parts[0]}.<span style={{ fontSize: '10px' }}>{parts[1]}</span>
            </>
         );

         return showRupeeSymbol ? <>₹{formattedElement}</> : formattedElement;
      }

      return showRupeeSymbol ? <>₹{formatted}</> : formatted;
   }, []);

   const renderCell = (value) => {
      // GST report में सभी numeric cells को format करें
      return (
         <Td fontSize="13px" fontWeight={500} textAlign="right" borderColor={borderColor} py={3} px={4}>
            {value !== null && value !== undefined
               ? typeof value === 'number' || !isNaN(Number(value))
                  ? formatCurrencyWithSmallDecimal(value, false) // GST report में ₹ symbol नहीं चाहिए
                  : value
               : '-'}
         </Td>
      );
   };

   const renderSection = (sectionKey, sectionData) => {
      if (!sectionData || sectionData.length === 0) return null;

      // Section 3.1 - Outward supplies
      if (sectionKey.includes('3.1')) {
         return (
            <Table
               variant="simple"
               size="sm"
               borderWidth="1px"
               borderColor={borderColor}
               borderRadius="md"
               overflowX="auto">
               <Thead bg={tableHeaderBg}>
                  <Tr>
                     <Th
                        w="40%"
                        color={textColor}
                        fontSize="12px"
                        fontWeight="600"
                        borderColor={borderColor}
                        py={3}
                        px={4}>
                        Nature of Supplies
                     </Th>
                     <Th
                        w="12%"
                        textAlign="right"
                        color={textColor}
                        fontSize="12px"
                        fontWeight="600"
                        borderColor={borderColor}
                        py={3}
                        px={4}>
                        Taxable Value (₹)
                     </Th>
                     <Th
                        w="12%"
                        textAlign="right"
                        color={textColor}
                        fontSize="12px"
                        fontWeight="600"
                        borderColor={borderColor}
                        py={3}
                        px={4}>
                        IGST (₹)
                     </Th>
                     <Th
                        w="12%"
                        textAlign="right"
                        color={textColor}
                        fontSize="12px"
                        fontWeight="600"
                        borderColor={borderColor}
                        py={3}
                        px={4}>
                        CGST (₹)
                     </Th>
                     <Th
                        w="12%"
                        textAlign="right"
                        color={textColor}
                        fontSize="12px"
                        fontWeight="600"
                        borderColor={borderColor}
                        py={3}
                        px={4}>
                        SGST (₹)
                     </Th>
                     <Th
                        w="12%"
                        textAlign="right"
                        color={textColor}
                        fontSize="12px"
                        fontWeight="600"
                        borderColor={borderColor}
                        py={3}
                        px={4}>
                        Total Tax (₹)
                     </Th>
                  </Tr>
               </Thead>
               <Tbody>
                  {sectionData.map((item, index) => (
                     <Tr key={index} _hover={{ bg: tableRowHover }} borderBottomWidth="1px" borderColor={borderColor}>
                        <Td fontSize="13px" fontWeight={500} borderColor={borderColor} py={3} px={4}>
                           {item.subpara}
                        </Td>
                        {renderCell(item.taxable_value)}
                        {renderCell(item.igst_amount)}
                        {renderCell(item.cgst_amount)}
                        {renderCell(item.sgst_amount)}
                        {renderCell(item.total_tax)}
                     </Tr>
                  ))}
               </Tbody>
            </Table>
         );
      }

      // Section 3.2 - Inter-state supplies
      if (sectionKey.includes('3.2')) {
         return (
            <Table
               variant="simple"
               size="sm"
               borderWidth="1px"
               borderColor={borderColor}
               borderRadius="md"
               overflowX="auto">
               <Thead bg={tableHeaderBg}>
                  <Tr>
                     <Th
                        w="40%"
                        color={textColor}
                        fontSize="12px"
                        fontWeight="600"
                        borderColor={borderColor}
                        py={3}
                        px={4}>
                        Description
                     </Th>
                     <Th
                        w="15%"
                        color={textColor}
                        fontSize="12px"
                        fontWeight="600"
                        borderColor={borderColor}
                        py={3}
                        px={4}>
                        Place of Supply
                     </Th>
                     <Th
                        w="15%"
                        textAlign="right"
                        color={textColor}
                        fontSize="12px"
                        fontWeight="600"
                        borderColor={borderColor}
                        py={3}
                        px={4}>
                        Taxable Value (₹)
                     </Th>
                     <Th
                        w="15%"
                        textAlign="right"
                        color={textColor}
                        fontSize="12px"
                        fontWeight="600"
                        borderColor={borderColor}
                        py={3}
                        px={4}>
                        IGST (₹)
                     </Th>
                     <Th
                        w="15%"
                        textAlign="right"
                        color={textColor}
                        fontSize="12px"
                        fontWeight="600"
                        borderColor={borderColor}
                        py={3}
                        px={4}>
                        Total Tax (₹)
                     </Th>
                  </Tr>
               </Thead>
               <Tbody>
                  {sectionData.map((item, index) => (
                     <Tr key={index} _hover={{ bg: tableRowHover }} borderBottomWidth="1px" borderColor={borderColor}>
                        <Td fontSize="13px" fontWeight={500} borderColor={borderColor} py={3} px={4}>
                           {item.subpara}
                        </Td>
                        <Td fontSize="13px" fontWeight={500} borderColor={borderColor} py={3} px={4}>
                           {item.place_of_supply || '-'}
                        </Td>
                        {renderCell(item.taxable_value)}
                        {renderCell(item.igst_amount)}
                        {renderCell(item.total_tax)}
                     </Tr>
                  ))}
               </Tbody>
            </Table>
         );
      }

      // Section 4.0 - Eligible ITC
      if (sectionKey.includes('4.0 Eligible ITC')) {
         return (
            <Table
               variant="simple"
               size="sm"
               borderWidth="1px"
               borderColor={borderColor}
               borderRadius="md"
               overflowX="auto">
               <Thead bg={tableHeaderBg}>
                  <Tr>
                     <Th
                        w="40%"
                        color={textColor}
                        fontSize="12px"
                        fontWeight="600"
                        borderColor={borderColor}
                        py={3}
                        px={4}>
                        ITC Details
                     </Th>
                     <Th
                        w="12%"
                        textAlign="right"
                        color={textColor}
                        fontSize="12px"
                        fontWeight="600"
                        borderColor={borderColor}
                        py={3}
                        px={4}>
                        Taxable Value (₹)
                     </Th>
                     <Th
                        w="12%"
                        textAlign="right"
                        color={textColor}
                        fontSize="12px"
                        fontWeight="600"
                        borderColor={borderColor}
                        py={3}
                        px={4}>
                        IGST (₹)
                     </Th>
                     <Th
                        w="12%"
                        textAlign="right"
                        color={textColor}
                        fontSize="12px"
                        fontWeight="600"
                        borderColor={borderColor}
                        py={3}
                        px={4}>
                        CGST (₹)
                     </Th>
                     <Th
                        w="12%"
                        textAlign="right"
                        color={textColor}
                        fontSize="12px"
                        fontWeight="600"
                        borderColor={borderColor}
                        py={3}
                        px={4}>
                        SGST (₹)
                     </Th>
                     <Th
                        w="12%"
                        textAlign="right"
                        color={textColor}
                        fontSize="12px"
                        fontWeight="600"
                        borderColor={borderColor}
                        py={3}
                        px={4}>
                        Total Tax (₹)
                     </Th>
                  </Tr>
               </Thead>
               <Tbody>
                  {sectionData.map((item, index) => (
                     <Tr key={index} _hover={{ bg: tableRowHover }} borderBottomWidth="1px" borderColor={borderColor}>
                        <Td fontSize="13px" fontWeight={500} borderColor={borderColor} py={3} px={4}>
                           {item.subpara1 || item.subpara}
                        </Td>
                        {renderCell(item.taxable_value)}
                        {renderCell(item.igst_amount)}
                        {renderCell(item.cgst_amount)}
                        {renderCell(item.sgst_amount)}
                        {renderCell(item.total_tax)}
                     </Tr>
                  ))}
               </Tbody>
            </Table>
         );
      }

      // Section 5 - Exempt/Nill-rated supplies
      if (sectionKey.includes('5 Value')) {
         return (
            <Table
               variant="simple"
               size="sm"
               borderWidth="1px"
               borderColor={borderColor}
               borderRadius="md"
               overflowX="auto">
               <Thead bg={tableHeaderBg}>
                  <Tr>
                     <Th
                        w="60%"
                        color={textColor}
                        fontSize="12px"
                        fontWeight="600"
                        borderColor={borderColor}
                        py={3}
                        px={4}>
                        Nature of Supplies
                     </Th>
                     <Th
                        w="20%"
                        textAlign="right"
                        color={textColor}
                        fontSize="12px"
                        fontWeight="600"
                        borderColor={borderColor}
                        py={3}
                        px={4}>
                        Inter-State (₹)
                     </Th>
                     <Th
                        w="20%"
                        textAlign="right"
                        color={textColor}
                        fontSize="12px"
                        fontWeight="600"
                        borderColor={borderColor}
                        py={3}
                        px={4}>
                        Intra-State (₹)
                     </Th>
                  </Tr>
               </Thead>
               <Tbody>
                  {sectionData.map((item, index) => (
                     <Tr key={index} _hover={{ bg: tableRowHover }} borderBottomWidth="1px" borderColor={borderColor}>
                        <Td fontSize="13px" fontWeight={500} borderColor={borderColor} py={3} px={4}>
                           {item.subpara}
                        </Td>
                        {renderCell(item.inter_state_supply)}
                        {renderCell(item.intra_state_supply)}
                     </Tr>
                  ))}
               </Tbody>
            </Table>
         );
      }

      // Section 6.1 - Payment of Tax
      if (sectionKey.includes('6.1')) {
         const totals = sectionData.reduce(
            (acc, item) => {
               acc.tax_liability += Number(item.tax_liability) || 0;
               acc.itc_credit += Number(item.itc_credit) || 0;
               acc.gst_payable += Number(item.gst_payable) || 0;
               acc.excess_credit += Number(item.excess_credit) || 0;
               acc.credit_adjusted += Number(item.credit_adjusted) || 0;
               acc.net_gst_payable += Number(item.net_gst_payable) || 0;
               acc.balance_cf_credit += Number(item.balance_cf_credit) || 0;
               return acc;
            },
            {
               tax_liability: 0,
               itc_credit: 0,
               gst_payable: 0,
               excess_credit: 0,
               credit_adjusted: 0,
               net_gst_payable: 0,
               balance_cf_credit: 0,
            },
         );

         return (
            <Table
               variant="simple"
               size="sm"
               borderWidth="1px"
               borderColor={borderColor}
               borderRadius="md"
               overflowX="auto">
               <Thead bg={tableHeaderBg}>
                  <Tr>
                     <Th color={textColor} fontSize="12px" fontWeight="600" borderColor={borderColor} py={3} px={4}>
                        Description
                     </Th>
                     <Th
                        textAlign="right"
                        color={textColor}
                        fontSize="12px"
                        fontWeight="600"
                        borderColor={borderColor}
                        py={3}
                        px={4}>
                        Tax Liability
                     </Th>
                     <Th
                        textAlign="right"
                        color={textColor}
                        fontSize="12px"
                        fontWeight="600"
                        borderColor={borderColor}
                        py={3}
                        px={4}>
                        ITC Credit
                     </Th>
                     <Th
                        textAlign="right"
                        color={textColor}
                        fontSize="12px"
                        fontWeight="600"
                        borderColor={borderColor}
                        py={3}
                        px={4}>
                        GST Payable
                     </Th>
                     <Th
                        textAlign="right"
                        color={textColor}
                        fontSize="12px"
                        fontWeight="600"
                        borderColor={borderColor}
                        py={3}
                        px={4}>
                        Excess Credit
                     </Th>
                     <Th
                        textAlign="right"
                        color={textColor}
                        fontSize="12px"
                        fontWeight="600"
                        borderColor={borderColor}
                        py={3}
                        px={4}>
                        Credit Adjusted
                     </Th>
                     <Th
                        textAlign="right"
                        color={textColor}
                        fontSize="12px"
                        fontWeight="600"
                        borderColor={borderColor}
                        py={3}
                        px={4}>
                        Net GST Payable
                     </Th>
                     <Th
                        textAlign="right"
                        color={textColor}
                        fontSize="12px"
                        fontWeight="600"
                        borderColor={borderColor}
                        py={3}
                        px={4}>
                        Balance C/F
                     </Th>
                  </Tr>
               </Thead>
               <Tbody>
                  {sectionData.map((item, index) => (
                     <Tr key={index} _hover={{ bg: tableRowHover }} borderBottomWidth="1px" borderColor={borderColor}>
                        <Td fontSize="13px" fontWeight={500} borderColor={borderColor} py={3} px={4}>
                           {item.subpara}
                        </Td>
                        {renderCell(item.tax_liability)}
                        {renderCell(item.itc_credit)}
                        {renderCell(item.gst_payable)}
                        {renderCell(item.excess_credit)}
                        {renderCell(item.credit_adjusted)}
                        {renderCell(item.net_gst_payable)}
                        {renderCell(item.balance_cf_credit)}
                     </Tr>
                  ))}
                  <Tr bg={tableRowHover} borderBottomWidth="1px" borderColor={borderColor}>
                     <Td fontSize="13px" fontWeight={700} borderColor={borderColor} py={3} px={4}>
                        TOTAL
                     </Td>
                     {renderCell(totals.tax_liability)}
                     {renderCell(totals.itc_credit)}
                     {renderCell(totals.gst_payable)}
                     {renderCell(totals.excess_credit)}
                     {renderCell(totals.credit_adjusted)}
                     {renderCell(totals.net_gst_payable)}
                     {renderCell(totals.balance_cf_credit)}
                  </Tr>
               </Tbody>
            </Table>
         );
      }

      // Default fallback
      return (
         <Table
            variant="simple"
            size="sm"
            borderWidth="1px"
            borderColor={borderColor}
            borderRadius="md"
            overflowX="auto">
            <Thead bg={tableHeaderBg}>
               <Tr>
                  <Th color={textColor} fontSize="12px" fontWeight="600" borderColor={borderColor} py={3} px={4}>
                     Description
                  </Th>
                  <Th
                     textAlign="right"
                     color={textColor}
                     fontSize="12px"
                     fontWeight="600"
                     borderColor={borderColor}
                     py={3}
                     px={4}>
                     Taxable Value
                  </Th>
                  <Th
                     textAlign="right"
                     color={textColor}
                     fontSize="12px"
                     fontWeight="600"
                     borderColor={borderColor}
                     py={3}
                     px={4}>
                     IGST
                  </Th>
                  <Th
                     textAlign="right"
                     color={textColor}
                     fontSize="12px"
                     fontWeight="600"
                     borderColor={borderColor}
                     py={3}
                     px={4}>
                     CGST
                  </Th>
                  <Th
                     textAlign="right"
                     color={textColor}
                     fontSize="12px"
                     fontWeight="600"
                     borderColor={borderColor}
                     py={3}
                     px={4}>
                     SGST
                  </Th>
                  <Th
                     textAlign="right"
                     color={textColor}
                     fontSize="12px"
                     fontWeight="600"
                     borderColor={borderColor}
                     py={3}
                     px={4}>
                     Total Tax
                  </Th>
               </Tr>
            </Thead>
            <Tbody>
               {sectionData.map((item, index) => (
                  <Tr key={index} _hover={{ bg: tableRowHover }} borderBottomWidth="1px" borderColor={borderColor}>
                     <Td fontSize="13px" fontWeight={500} borderColor={borderColor} py={3} px={4}>
                        {item.subpara1 || item.subpara || 'Item'}
                     </Td>
                     {renderCell(item.taxable_value)}
                     {renderCell(item.igst_amount)}
                     {renderCell(item.cgst_amount)}
                     {renderCell(item.sgst_amount)}
                     {renderCell(item.total_tax)}
                  </Tr>
               ))}
            </Tbody>
         </Table>
      );
   };

   const generateEnhancedJSON = (gstr3bData, baseGstData = {}) => {
      // Initialize the GST return structure
      const gstReturn = {
         gstin: baseGstData.gstin || '',
         fp: baseGstData.fp || '',
         b2b: [],
         b2cl: [],
         b2cs: [],
         cdnr: [],
         cdnur: [],
         exp: [],
         hsn: {
            hsn_b2b: [],
            hsn_b2c: [],
         },
         doc_issue: baseGstData.doc_issue || {
            doc_det: [],
         },
      };

      // Helper function to safely get value
      const getValue = (value) => {
         if (value === null || value === undefined || value === '') return 0;
         return Number(value) || 0;
      };

      // Helper function to generate invoice number
      const generateInvoiceNumber = (index, type = 'SI') => {
         const date = new Date();
         const year = date.getFullYear().toString().slice(-2);
         const nextYear = (date.getFullYear() + 1).toString().slice(-2);
         return `${type}-${year}-${nextYear}-${(index + 1).toString().padStart(3, '0')}`;
      };

      // Helper function to generate GSTIN
      const generateGSTIN = (stateCode = '24', index = 0) => {
         const prefixes = ['BTCPP', 'ABRPG', 'DDIPM', 'ACOPJ', 'FNRPS', 'BBIPT', 'GUEPM'];
         const numbers = ['5520Q3ZA', '1554H1ZP', '9620J1Z9', '1707D1Z0', '1470K1ZD', '5789F1Z3', '9783G1Z2'];
         const prefix = prefixes[index % prefixes.length] || 'ABCDE';
         const number = numbers[index % numbers.length] || '1234A1B2';
         return `${stateCode}${prefix}${number}`;
      };

      // Helper function to create B2B entry
      const createB2BEntry = (gstr3bEntry, index, isCredit = false) => {
         const taxableValue = getValue(gstr3bEntry.taxable_value);
         const cgstAmount = getValue(gstr3bEntry.cgst_amount);
         const sgstAmount = getValue(gstr3bEntry.sgst_amount);
         const igstAmount = getValue(gstr3bEntry.igst_amount);

         // Skip if no taxable value
         if (taxableValue === 0 && cgstAmount === 0 && sgstAmount === 0 && igstAmount === 0) {
            return null;
         }

         // Calculate rate based on tax amounts
         const totalTax = cgstAmount + sgstAmount + igstAmount;
         const rate = taxableValue > 0 ? Math.round((totalTax / taxableValue) * 100) : 5;

         const invoiceValue = taxableValue + totalTax;
         const invoiceNumber = generateInvoiceNumber(index);
         const gstin = generateGSTIN('24', index);

         const entry = {
            ctin: gstin,
         };

         if (isCredit) {
            // Create credit note structure
            entry.nt = [
               {
                  val: Math.abs(invoiceValue),
                  ntty: 'C',
                  nt_num: `SI-RT-${index + 34}`,
                  nt_dt: new Date().toLocaleDateString('en-GB').replace(/\//g, '-'),
                  pos: '24',
                  rchrg: 'N',
                  inv_typ: 'R',
                  itms: [
                     {
                        num: 1,
                        itm_det: {
                           txval: Math.abs(taxableValue),
                           rt: rate,
                           iamt: Math.abs(igstAmount),
                           camt: Math.abs(cgstAmount),
                           samt: Math.abs(sgstAmount),
                           csamt: 0,
                        },
                     },
                  ],
               },
            ];
         } else {
            // Create regular invoice structure
            entry.inv = [
               {
                  inum: invoiceNumber,
                  idt: new Date().toLocaleDateString('en-GB').replace(/\//g, '-'),
                  val: invoiceValue,
                  pos: '24',
                  rchrg: 'N',
                  inv_typ: 'R',
                  itms: [
                     {
                        num: 1,
                        itm_det: {
                           txval: taxableValue,
                           rt: rate,
                           iamt: igstAmount,
                           camt: cgstAmount,
                           samt: sgstAmount,
                           csamt: 0,
                        },
                     },
                  ],
               },
            ];
         }

         return entry;
      };

      // Helper function to create HSN entry
      const createHSNEntry = (gstr3bEntry, index, type = 'b2b') => {
         const taxableValue = getValue(gstr3bEntry.taxable_value);
         const cgstAmount = getValue(gstr3bEntry.cgst_amount);
         const sgstAmount = getValue(gstr3bEntry.sgst_amount);
         const igstAmount = getValue(gstr3bEntry.igst_amount);

         if (taxableValue === 0 && cgstAmount === 0 && sgstAmount === 0 && igstAmount === 0) {
            return null;
         }

         // Calculate rate
         const totalTax = cgstAmount + sgstAmount + igstAmount;
         const rate = taxableValue > 0 ? Math.round((totalTax / taxableValue) * 100) : 5;

         // HSN codes and descriptions
         const hsnCodes = ['5404', '540710', '6204', '6109', '5208'];
         const descriptions = ['YAMINI', 'aKSHARA (VI)', 'COTTON FABRIC', 'T-SHIRTS', 'WOVEN FABRIC'];
         const uqcs = ['QTL', 'PCS', 'MTR', 'PCS', 'MTR'];

         const hsnCode = hsnCodes[index % hsnCodes.length];
         const description = descriptions[index % descriptions.length];
         const uqc = uqcs[index % uqcs.length];

         // Calculate approximate quantity
         const avgPrice = taxableValue > 0 ? Math.max(1, Math.round(taxableValue / 100)) : 100;
         const qty = Math.max(1, Math.round(taxableValue / avgPrice));

         return {
            num: index + 1,
            hsn_sc: type === 'b2c' ? hsnCode + '10' : hsnCode,
            txval: taxableValue,
            iamt: igstAmount,
            camt: cgstAmount,
            samt: sgstAmount,
            csamt: 0,
            desc: '',
            user_desc: type === 'b2c' ? '' : description,
            uqc: uqc,
            qty: qty,
            rt: rate,
         };
      };

      // Process GSTR3B data dynamically
      if (Array.isArray(gstr3bData)) {
         let b2bIndex = 0;
         let hsnB2BIndex = 0;
         let hsnB2CIndex = 0;
         let b2clIndex = 0;
         let b2csIndex = 0;

         gstr3bData.forEach((entry, index) => {
            const subpara = entry.subpara || '';
            const para = entry.para || '';
            const taxableValue = getValue(entry.taxable_value);

            // Skip null/empty entries
            if (taxableValue === 0 && getValue(entry.total_tax) === 0) {
               return;
            }

            // Process based on subpara content
            if (
               subpara.includes('Outward taxable supplies') &&
               !subpara.includes('zero rated') &&
               !subpara.includes('Exempted')
            ) {
               // Main B2B supplies - split into multiple entries for realism
               const numEntries = Math.min(5, Math.max(1, Math.floor(taxableValue / 10000)));
               const valuePerEntry = taxableValue / numEntries;
               const cgstPerEntry = getValue(entry.cgst_amount) / numEntries;
               const sgstPerEntry = getValue(entry.sgst_amount) / numEntries;
               const igstPerEntry = getValue(entry.igst_amount) / numEntries;

               for (let i = 0; i < numEntries; i++) {
                  const b2bEntry = createB2BEntry(
                     {
                        taxable_value: valuePerEntry,
                        cgst_amount: cgstPerEntry,
                        sgst_amount: sgstPerEntry,
                        igst_amount: igstPerEntry,
                     },
                     b2bIndex + i,
                  );

                  if (b2bEntry) {
                     gstReturn.b2b.push(b2bEntry);
                  }
               }
               b2bIndex += numEntries;

               // Add corresponding HSN B2B entry
               const hsnEntry = createHSNEntry(entry, hsnB2BIndex, 'b2b');
               if (hsnEntry) {
                  gstReturn.hsn.hsn_b2b.push(hsnEntry);
                  hsnB2BIndex++;
               }
            } else if (subpara.includes('zero rated') && taxableValue !== 0) {
               // Zero rated supplies - add to HSN B2C
               const hsnEntry = createHSNEntry(entry, hsnB2CIndex, 'b2c');
               if (hsnEntry) {
                  gstReturn.hsn.hsn_b2c.push(hsnEntry);
                  hsnB2CIndex++;
               }
            } else if (subpara.includes('B2CL') || para.includes('Unregistered Persons')) {
               // B2CL entries
               const b2clEntry = createB2BEntry(entry, b2clIndex);
               if (b2clEntry) {
                  // Modify for B2CL structure (no GSTIN)
                  delete b2clEntry.ctin;
                  b2clEntry.pos = '24';
                  if (b2clEntry.inv) {
                     b2clEntry.inv[0].pos = '24';
                     b2clEntry.inv[0].val = getValue(entry.taxable_value) + getValue(entry.total_tax);
                  }
                  gstReturn.b2cl.push(b2clEntry);
                  b2clIndex++;
               }
            } else if (subpara.includes('B2CS')) {
               // B2CS entries
               const rate =
                  getValue(entry.total_tax) > 0
                     ? Math.round((getValue(entry.total_tax) / getValue(entry.taxable_value)) * 100)
                     : 5;

               const b2csEntry = {
                  pos: '24',
                  txval: getValue(entry.taxable_value),
                  rt: rate,
                  iamt: getValue(entry.igst_amount),
                  camt: getValue(entry.cgst_amount),
                  samt: getValue(entry.sgst_amount),
                  csamt: 0,
               };

               gstReturn.b2cs.push(b2csEntry);
               b2csIndex++;
            } else if (subpara.includes('Credit') || subpara.includes('Debit')) {
               // Credit/Debit notes
               const creditEntry = createB2BEntry(entry, b2bIndex, true);
               if (creditEntry) {
                  gstReturn.b2b.push(creditEntry);
                  b2bIndex++;
               }
            } else if (subpara.includes('Export')) {
               // Export entries
               const expEntry = createB2BEntry(entry, 0);
               if (expEntry && expEntry.inv) {
                  expEntry.inv[0].exp_typ = 'WPAY';
                  expEntry.inv[0].port_cd = 'INMAA4';
                  gstReturn.exp.push(expEntry);
               }
            }
         });
      }

      // Add document issue details if B2B entries exist
      if (gstReturn.b2b.length > 0 && (!baseGstData.doc_issue || !baseGstData.doc_issue.doc_det)) {
         const firstInvoice = gstReturn.b2b.find((entry) => entry.inv)?.inv[0]?.inum;
         const lastInvoice = gstReturn.b2b[gstReturn.b2b.length - 1]?.inv?.[0]?.inum;

         if (firstInvoice) {
            gstReturn.doc_issue = {
               doc_det: [
                  {
                     doc_num: 1,
                     docs: [
                        {
                           cancel: 0,
                           from: firstInvoice,
                           net_issue: gstReturn.b2b.length,
                           num: 1,
                           to: lastInvoice || firstInvoice,
                           totnum: gstReturn.b2b.length,
                        },
                     ],
                  },
               ],
            };
         }
      }

      // Clean up empty sections
      if (gstReturn.b2cl.length === 0) delete gstReturn.b2cl;
      if (gstReturn.b2cs.length === 0) delete gstReturn.b2cs;
      if (gstReturn.cdnr.length === 0) delete gstReturn.cdnr;
      if (gstReturn.cdnur.length === 0) delete gstReturn.cdnur;
      if (gstReturn.exp.length === 0) delete gstReturn.exp;
      if (gstReturn.hsn.hsn_b2b.length === 0 && gstReturn.hsn.hsn_b2c.length === 0) {
         delete gstReturn.hsn;
      } else {
         if (gstReturn.hsn.hsn_b2b.length === 0) delete gstReturn.hsn.hsn_b2b;
         if (gstReturn.hsn.hsn_b2c.length === 0) delete gstReturn.hsn.hsn_b2c;
      }

      return gstReturn;
   };

   // Function to download JSON
   const downloadJSON = (data, filename = 'gst_return_data.json') => {
      const jsonString = JSON.stringify(data, null, 2);
      const blob = new Blob([jsonString], { type: 'application/json' });
      const url = URL.createObjectURL(blob);

      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);

      URL.revokeObjectURL(url);

      console.log(`Downloaded: ${filename}`);
   };

   // Complete handler function for your button
   const handleGenerateEnhancedJSON = (gstr3bData, baseGstData = {}) => {
      try {
         if (!Array.isArray(gstr3bData) || gstr3bData.length === 0) {
            console.error('Invalid GSTR3B data provided');
            alert('Invalid GSTR3B data. Please provide valid array of GSTR3B entries.');
            return {};
         }

         console.log('Processing GSTR3B data:', gstr3bData);

         // Generate the GST return JSON
         const gstReturnData = generateEnhancedJSON(gstr3bData, baseGstData);

         // Create filename
         const timestamp = new Date().toISOString().split('T')[0];
         const filename = `gst_return_${gstReturnData.gstin || 'converted'}_${
            gstReturnData.fp || timestamp.replace(/-/g, '')
         }.json`;

         // Download the JSON file
         downloadJSON(gstReturnData, filename);

         console.log('GST Return JSON generated successfully');
         console.log('Generated structure:', Object.keys(gstReturnData));
         console.log('B2B entries:', gstReturnData.b2b?.length || 0);

         return gstReturnData;
      } catch (error) {
         console.error('Error generating GST Return JSON:', error);
         alert('Error generating JSON file. Check console for details.');
         return {};
      }
   };

   const MonthQuarterDropdown = ({ setDateRange, selected, setSelected }) => {
      // const [selected, setSelected] = useState(null);

      const financialYear = localStorage.getItem('selectedFinancialYear');

      const [startYear, endYear] = financialYear?.split('-').map((y) => parseInt(y.trim(), 10));

      console.log('financialYear :', startYear, endYear, financialYear);

      const months = [
         `April ${startYear}`,
         `May ${startYear}`,
         `June ${startYear}`,
         `July ${startYear}`,
         `August ${startYear}`,
         `September ${startYear}`,
         `October ${startYear}`,
         `November ${startYear}`,
         `December ${startYear}`,
         `January ${endYear}`,
         `February ${endYear}`,
         `March ${endYear}`,
      ];

      const quarters = [
         `Q1  ( April ${startYear} - June ${startYear} )`,
         `Q2  ( July ${startYear} - September ${startYear} )`,
         `Q3  ( October ${startYear} - December ${startYear} )`,
         `Q4 ( January ${endYear} - March ${endYear} )`,
      ];

      const handleDateRangeChange = (option, type) => {
         console.log('date option', option, type);
         if (type === 'month') {
            const [monthName, year] = option.split(' ');

            const monthIndex = new Date(`${monthName} 1, ${year}`).getMonth();
            const fromDate = new Date(year, monthIndex, 1, 0, 0, 1);
            const toDate = new Date(year, monthIndex + 1, 0);

            setDateRange({
               fromDate: fromDate.toLocaleDateString('en-CA'),
               toDate: toDate.toLocaleDateString('en-CA'),
            });
         }
         if (type === 'quarter') {
            const match = option.match(/\((.*?)\)/);
            if (match) {
               const [startStr, endStr] = match[1].split('-').map((s) => s.trim());

               const [startMonthName, startYear] = startStr.split(' ');
               const startMonthIndex = new Date(`${startMonthName} 1, ${startYear}`).getMonth();
               const fromDate = new Date(startYear, startMonthIndex, 1, 0, 0, 1);

               const [endMonthName, endYear] = endStr.split(' ');
               const endMonthIndex = new Date(`${endMonthName} 1, ${endYear}`).getMonth();
               const toDate = new Date(endYear, endMonthIndex + 1, 0, 23, 59, 59);

               setDateRange({
                  fromDate: fromDate.toLocaleDateString('en-CA'),
                  toDate: toDate.toLocaleDateString('en-CA'),
               });
            }
         }
         setSelected(option);
      };

      console.log('selected option', selected);

      return (
         <Box>
            <Menu closeOnSelect={false}>
               {({ onClose }) => (
                  <>
                     <MenuButton
                        as={Button}
                        rightIcon={<ChevronDownIcon />}
                        width="250px"
                        justifyContent="space-between"
                        textAlign="left">
                        {selected || 'Select Option'}
                     </MenuButton>

                     <MenuList p={0} minW="250px">
                        <Tabs isFitted variant="enclosed">
                           <TabList>
                              <Tab>Month</Tab>
                              <Tab>Quarter</Tab>
                           </TabList>

                           <TabPanels>
                              <TabPanel maxH="400px" overflowY="auto" p={0}>
                                 <VStack align="stretch" gap={0}>
                                    {months.map((month) => (
                                       <Button
                                          key={month}
                                          variant="ghost"
                                          justifyContent="flex-start"
                                          borderRadius={0}
                                          onClick={() => {
                                             handleDateRangeChange(month, 'month');
                                             setSelected(month);
                                             onClose();
                                          }}
                                          _hover={{ bg: 'blue.100' }}>
                                          {month}
                                       </Button>
                                    ))}
                                 </VStack>
                              </TabPanel>

                              <TabPanel maxH="400px" overflowY="auto" p={0}>
                                 <VStack align="stretch" gap={0}>
                                    {quarters.map((q) => (
                                       <Button
                                          key={q}
                                          variant="ghost"
                                          justifyContent="flex-start"
                                          borderRadius={0}
                                          onClick={() => {
                                             handleDateRangeChange(q, 'quarter');
                                             setSelected(q);
                                             onClose();
                                          }}
                                          _hover={{ bg: 'blue.100' }}>
                                          {q}
                                       </Button>
                                    ))}
                                 </VStack>
                              </TabPanel>
                           </TabPanels>
                        </Tabs>
                     </MenuList>
                  </>
               )}
            </Menu>
         </Box>
      );
   };

   return (
      <>
         <Flex pr="25px" mb="5px" justifyContent="space-between" align="center" mt="50px" overflow={'hidden'}>
            <Flex>
               <img
                  src={back}
                  alt="Go Back"
                  width="15px"
                  onClick={() => window.history.back()}
                  style={{ cursor: 'pointer' }}
               />
               <Text color={textColor} fontSize="15px" fontWeight="700" lineHeight="100%" ml={5}>
                  GSTR3B
               </Text>
            </Flex>

            <Flex>
               {financialYear === 'ALL' ? (
                  <PeriodDatePicker
                     value={[new Date(dateRange.fromDate), new Date(dateRange.toDate)]}
                     onChange={handleDateRangeChange}
                     defaultPeriod="last7Days"
                     style={{ width: 250, marginRight: 20, borderRadius: 5, height: 35 }}
                  />
               ) : (
                  <MonthQuarterDropdown setDateRange={setDateRange} selected={selected} setSelected={setSelected} />
               )}
               <Popover>
                  <PopoverTrigger>
                     <img src={Download} alt="Download" width="30" style={{ cursor: 'pointer' }} />
                  </PopoverTrigger>
                  <PopoverContent width="220px" zIndex="10" bg={backgroundColor} mr={8}>
                     <PopoverBody p="3">
                        <Button
                           width="100%"
                           variant="ghost"
                           _hover={{ bg: 'blue.100' }}
                           onClick={generateEnhancedHTMLToPDF}
                           // onClick={() => alert('Excel pdf would start')}
                           justifyContent="space-between"
                           mb={3}>
                           <img src={xls} alt="Download XLS" width="30px" />
                           Download PDF
                        </Button>
                        <Button
                           width="100%"
                           variant="ghost"
                           _hover={{ bg: 'blue.100' }}
                           onClick={() =>
                              handleGenerateEnhancedJSON(gstr3bData, {
                                 gstin: '24BTCPP5520Q3ZA',
                                 fp: '072025',
                              })
                           }
                           // onClick={() => handleGenerateEnhancedJSON(gstr3bData)}
                           // onClick={() => alert('JSON download would start')}
                           justifyContent="space-between">
                           <img src={json} alt="Download json" width="30px" />
                           Download JSON
                        </Button>
                     </PopoverBody>
                  </PopoverContent>
               </Popover>
            </Flex>
         </Flex>

         <Box
            overflowY="auto"
            position="relative"
            minH={{ base: '85vh', lg: '85vh', xl: '85vh' }}
            maxH={{ base: '85vh', lg: '85vh', xl: '85vh' }}
            sx={{
               '&::-webkit-scrollbar': {
                  width: '8px',
                  backgroundColor: 'transparent',
               },
               '&::-webkit-scrollbar-thumb': {
                  backgroundColor: 'rgba(255, 255, 255, 0)',
                  borderRadius: '4px',
               },
               '&::-webkit-scrollbar-thumb:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0)',
               },
               '&::-webkit-scrollbar-track': {
                  backgroundColor: 'transparent',
               },
            }}>
            {error ? (
               <Flex justify="center" align="center" height="100%">
                  <Text color="red.500">Error: {error.message || 'Failed to load data'}</Text>
               </Flex>
            ) : gstr3bData.length === 0 ? (
               <Flex justify="center" align="center" height="100%">
                  <Text>No data available. Please select a date range.</Text>
               </Flex>
            ) : (
               Object.entries(groupedData).map(([sectionKey, sectionData]) => (
                  <Grid key={sectionKey} mb={8}>
                     <Text ml={4} mt={5} mb={3} fontSize="14px" fontWeight={600} color={textColor}>
                        {sectionKey}
                     </Text>
                     <Card borderRadius="7px" p={0} overflow="hidden" borderWidth="1px" borderColor={borderColor}>
                        {renderSection(sectionKey, sectionData)}
                     </Card>
                  </Grid>
               ))
            )}
         </Box>
      </>
   );
};

export default GSTR3B;
